/**
 * MainMenu for lbbw-am.de
 *
 * @author Florian Gutwald
 * Converted to Plain JS for Rollup
 */
export default class MainMenu {
  /**
   * MainMenu constructor
   *
   * @param {HTMLElement} element - The main navigation container
   */
  constructor(element) {
    this.element = element;
    this.body = document.body;
    this.events = new AbortController();
    this.mainmenu = document.getElementById('mainmenu');

    if (!this.mainmenu) {
      console.warn('MainMenu: #mainmenu not found');
      return;
    }

    this.mainmenuItems = Array.from(this.mainmenu.querySelectorAll('.mainmenu-list-item__lv1'));
    this.flyoutLinks = Array.from(this.mainmenu.querySelectorAll('.flyout-menu-item-link'));

    this.init();
  }

  /**
   * Initialize the menu
   */
  init() {
    console.log('MainMenu initialized');
    // Initialize tabindex for all flyout links
    this.flyoutLinks.forEach(link => link.setAttribute('tabindex', '-1'));

    // Event listeners
    this.attachMainMenuClickHandler();
    this.attachMainMenuKeydownHandler();
    this.attachNavlinkKeydownHandler();
    this.attachFlyoutLinkKeydownHandler();
    this.attachCloseButtonHandler();
    this.attachOutsideClickHandler();
    this.attachResizeHandler();
  }

  /**
   * Handle clicks on main menu items
   */
  attachMainMenuClickHandler() {
    this.mainmenu.addEventListener('click', (event) => {
      // Find the closest list item
      const listItem = event.target.closest('.mainmenu-list-item__lv1');
      if (!listItem) return;


      // Check if click was on navLink OR directly on list item
      const navLink = event.target.closest('.navlink');
      const hasFlyout = listItem.querySelector('.flyout') !== null;

      // If the list item has a flyout and we clicked on it (either navLink or list item itself)
      if (hasFlyout && (navLink || event.target === listItem)) {
        event.preventDefault();

        const isOpen = listItem.classList.contains('mainmenu-list-item__open');

        // Close other open flyouts
        this.mainmenuItems.forEach(item => {
          if (item !== listItem) {
            item.classList.remove('mainmenu-list-item__open');
            item.setAttribute('aria-expanded', 'false');
            item.querySelectorAll('.flyout-menu-item-link').forEach(link => {
              link.setAttribute('tabindex', '-1');
            });
          }
        });

        // Toggle current flyout
        if (!isOpen) {
          listItem.classList.add('mainmenu-list-item__open');
          listItem.setAttribute('aria-expanded', 'true');
          this.body.classList.add('openflyout');

          // Enable tabbing for links in current flyout
          listItem.querySelectorAll('.flyout-menu-item-link').forEach(link => {
            link.removeAttribute('tabindex');
          });
        } else {
          listItem.classList.remove('mainmenu-list-item__open');
          listItem.setAttribute('aria-expanded', 'false');

          // Disable tabbing for links in closing flyout
          listItem.querySelectorAll('.flyout-menu-item-link').forEach(link => {
            link.setAttribute('tabindex', '-1');
          });

          setTimeout(() => {
            if (this.events.signal.aborted) return;
            // Only remove openflyout if no other menu is open
            if (!this.mainmenu.querySelector('.mainmenu-list-item__open')) {
              this.body.classList.remove('openflyout');
            }
          }, 600);
        }
        return; // Stop further processing
      }

      // Handle direct click on flyout links (when they are tabbable)
      const flyoutLink = event.target.closest('.flyout-menu-item-link');
      if (flyoutLink && !flyoutLink.hasAttribute('tabindex')) {
        const href = flyoutLink.getAttribute('href');
        if (href) {
          window.location.href = href;
        }
      }
    }, { signal: this.events.signal });
  }

  /**
   * Handle keyboard navigation for menu items
   */
  attachMainMenuKeydownHandler() {
    this.mainmenu.addEventListener('keydown', (event) => {
      const listItem = event.target.closest('.mainmenu-list-item__lv1');
      if (!listItem) return;

      // Only handle if the keydown is directly on the list item or navlink
      const isListItemTarget = event.target === listItem;
      const isNavlinkTarget = event.target.classList.contains('navlink');

      if (!isListItemTarget && !isNavlinkTarget) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();

        const hasFlyout = listItem.querySelector('.flyout') !== null;
        const isOpen = listItem.classList.contains('mainmenu-list-item__open');

        if (hasFlyout) {
          // Close other flyouts
          this.mainmenuItems.forEach(item => {
            if (item !== listItem) {
              item.classList.remove('mainmenu-list-item__open');
              item.setAttribute('aria-expanded', 'false');
              item.querySelectorAll('.flyout-menu-item-link').forEach(link => {
                link.setAttribute('tabindex', '-1');
              });
            }
          });

          // Toggle current flyout
          if (!isOpen) {
            listItem.classList.add('mainmenu-list-item__open');
            listItem.setAttribute('aria-expanded', 'true');
            this.body.classList.add('openflyout');
            listItem.querySelectorAll('.flyout-menu-item-link').forEach(link => {
              link.removeAttribute('tabindex');
            });
          } else {
            listItem.classList.remove('mainmenu-list-item__open');
            listItem.setAttribute('aria-expanded', 'false');
            listItem.querySelectorAll('.flyout-menu-item-link').forEach(link => {
              link.setAttribute('tabindex', '-1');
            });

            setTimeout(() => {
              if (this.events.signal.aborted) return;
              if (!this.mainmenu.querySelector('.mainmenu-list-item__open')) {
                this.body.classList.remove('openflyout');
              }
            }, 600);
          }
        } else {
          // Navigate to link if no flyout
          const navlink = listItem.querySelector('.navlink');
          if (navlink) {
            const href = navlink.getAttribute('href');
            if (href) {
              window.location.href = href;
            }
          }
        }
      }
    }, { signal: this.events.signal });
  }

  /**
   * Handle keyboard navigation for navlinks
   */
  attachNavlinkKeydownHandler() {
    this.mainmenu.addEventListener('keydown', (event) => {
      if (!event.target.classList.contains('navlink')) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const parentItem = event.target.closest('.mainmenu-list-item__lv1');

        if (parentItem && parentItem.querySelector('.flyout')) {
          // Trigger the parent item's keydown handling
          const keyEvent = new KeyboardEvent('keydown', { key: event.key, bubbles: true });
          parentItem.dispatchEvent(keyEvent);
        } else {
          event.target.click();
        }
      }
    }, { signal: this.events.signal });
  }

  /**
   * Handle keyboard navigation for flyout menu links
   */
  attachFlyoutLinkKeydownHandler() {
    this.mainmenu.addEventListener('keydown', (event) => {
      if (!event.target.classList.contains('flyout-menu-item-link')) return;

      const currentLink = event.target;
      const flyout = currentLink.closest('.flyout');
      if (!flyout) return;

      const allFlyoutLinks = Array.from(flyout.querySelectorAll('.flyout-menu-item-link'));
      const currentIndex = allFlyoutLinks.indexOf(currentLink);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          const nextIndex = (currentIndex + 1) % allFlyoutLinks.length;
          allFlyoutLinks[nextIndex].focus();
          break;

        case 'ArrowUp':
          event.preventDefault();
          const prevIndex = currentIndex === 0 ? allFlyoutLinks.length - 1 : currentIndex - 1;
          allFlyoutLinks[prevIndex].focus();
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          currentLink.click();
          break;

        case 'Escape':
          event.preventDefault();
          this.closeAllFlyouts();
          break;
      }
    }, { signal: this.events.signal });
  }

  /**
   * Handle close button clicks
   */
  attachCloseButtonHandler() {
    this.mainmenu.addEventListener('click', (event) => {
      if (!event.target.closest('.js_flyoutClose')) return;

      const listItem = event.target.closest('.mainmenu-list-item__lv1');
      if (!listItem) return;

      listItem.classList.remove('mainmenu-list-item__open');
      listItem.setAttribute('aria-expanded', 'false');

      // Disable tabbing for links in closing flyout
      listItem.querySelectorAll('.flyout-menu-item-link').forEach(link => {
        link.setAttribute('tabindex', '-1');
      });

      setTimeout(() => {
        if (this.events.signal.aborted) return;
        if (!this.mainmenu.querySelector('.mainmenu-list-item__open')) {
          this.body.classList.remove('openflyout');
        }
      }, 600);
    }, { signal: this.events.signal });
  }

  /**
   * Close flyout on click outside
   */
  attachOutsideClickHandler() {
    document.addEventListener('click', (event) => {
      if (!event.target.closest('#mainmenu')) {
        if (this.mainmenu.querySelector('.mainmenu-list-item__open')) {
          this.closeAllFlyouts();
        }
      }
    }, { signal: this.events.signal });
  }

  /**
   * Close flyout on window resize (for mobile view switch)
   */
  attachResizeHandler() {
    window.addEventListener('resize', () => {
      const breakpoint = window.AMApp?.CONSTANTS?.BREAKPOINT?.LG || 1024;

      if (window.innerWidth <= breakpoint) {
        if (this.body.classList.contains('openflyout')) {
          this.closeAllFlyouts();
        }
      }
    }, { signal: this.events.signal });
  }

  destroy() {
    this.events.abort();
    if (this.mainmenu) this.closeAllFlyouts(true);
  }

  /**
   * Close all open flyouts
   */
  closeAllFlyouts(immediate = false) {
    this.mainmenuItems.forEach(item => {
      item.classList.remove('mainmenu-list-item__open');
      item.setAttribute('aria-expanded', 'false');
    });

    this.flyoutLinks.forEach(link => {
      link.setAttribute('tabindex', '-1');
    });

    if (immediate) this.body.classList.remove('openflyout');
    else setTimeout(() => {
      if (!this.events.signal.aborted) this.body.classList.remove('openflyout');
    }, 600);
  }
}

// Auto-init entfernen, da NavigationManager das übernimmt
